import React, { useState, useEffect, useRef } from "react";
import { TfiAlignCenter } from "react-icons/tfi";
import { useAssetContext } from "../../DataContext/AssetContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { datadogLogs } from "@datadog/browser-logs";
import RevenueGroupDropdown from "../../DropDowns/RevenueGroupDropdown/RevenueGroupDropdown";
import DateRangeDropdown from "../../DropDowns/DateRangeDropdown/DateRangeDropdown";
import GranularityDropdown from "../../DropDowns/GranularityDropdown/GranularityDropdown";
import ResourceDropdown from "../../DropDowns/ResourceDropdown/ResourceDropdown";
import GranularityAggregationToggle from '../../AssetDashboard/ConfigCharts/GranularityAggregationToggle';
import IntervalAlignmentToggle from '../../AssetDashboard/ConfigCharts/IntervalAlignmentToggle';
import { FiSearch } from "react-icons/fi";
import apiClient from "../../../services/apiClient";
import { getHeaders } from "../../ApiHeaders/apiHeaders";


interface AssetFiltersProps {
  activeTabLabel: string;
  assetSlug?: string; 
  refreshCounter?: number;
}

const AssetFilters: React.FC<AssetFiltersProps> = ({ activeTabLabel,refreshCounter }) => {
  const {
    selectedAsset,
    selectedResource,
    setSelectedResource,
    selectedDateRange,
    setSelectedDateRange,
    selectedFrequency,
    setSelectedFrequency,
    selectedRevenue,
    setSelectedRevenue,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setResourceNames,
    triggerSearch,
  } = useAssetContext();

  const [isCustomDate, setIsCustomDate] = useState(false);
  const [pendingDateRange, setPendingDateRange] = useState(selectedDateRange);
  const [pendingStartDate, setPendingStartDate] = useState<Date | null>(null);
  const { resetFilters } = useAssetContext();
  const [pendingEndDate, setPendingEndDate] = useState<Date | null>(null);
  const [pendingGranularity, setPendingGranularity] = useState(selectedFrequency);
  const [pendingResource, setPendingResource] = useState(selectedResource);
  const [pendingRevenue, setPendingRevenue] = useState(selectedRevenue);
  const customDateRangePattern = /^\d{4}-\d{2}-\d{2}\s-\s\d{4}-\d{2}-\d{2}$/;
  const previousAssetIdRef = useRef<string | number | undefined>();

  useEffect(() => {
    const assetId = selectedAsset?.asset_id;
    const assetChanged = previousAssetIdRef.current !== assetId;
    previousAssetIdRef.current = assetId;

    if (assetChanged) {
      setSelectedResource("ALL");
      setPendingResource("ALL");
      setResourceNames(["ALL"]);
    }

    const resources = Array.isArray(selectedAsset?.RESOURCES)
      ? selectedAsset.RESOURCES
      : [];
    const names = resources
      .map((resource: any) => resource.RESOURCE_NAME || resource.resource_name)
      .filter((name: unknown): name is string => typeof name === "string" && name.length > 0);

    if (names.length > 0) {
      const allResources = ["ALL", ...names];
      setResourceNames(allResources);
      const resourceSelection = assetChanged ? "ALL" : selectedResource;
      if (!names.includes(resourceSelection) && resourceSelection !== "ALL" && resourceSelection !== "") {
        setSelectedResource("");
      }
      return;
    }

    if (!selectedAsset?.asset_id) {
      setResourceNames(["ALL", "No Resources Available"]);
      return;
    }

    let cancelled = false;
    const loadResources = async () => {
      try {
        const headers = await getHeaders();
        const response = await apiClient.get(`/resources/${selectedAsset.asset_id}`, {
          headers,
          params: { page: 1, page_size: 100 },
        });
        const resourceData = response.data?.response?.data ?? response.data?.data ?? [];
        const resourceNames = resourceData
          .map((resource: any) => resource.RESOURCE_NAME || resource.resource_name)
          .filter((name: unknown): name is string => typeof name === "string" && name.length > 0);

        if (!cancelled) {
          setResourceNames(resourceNames.length ? ["ALL", ...resourceNames] : ["ALL", "No Resources Available"]);
        }
      } catch {
        if (!cancelled) {
          setResourceNames(["ALL", "No Resources Available"]);
        }
      }
    };

    loadResources();
    return () => {
      cancelled = true;
    };
  }, [selectedAsset, setResourceNames, selectedResource, setSelectedResource,refreshCounter]);

  const handleDateRangeChange = (value: string) => {
    datadogLogs.logger.info("Date Range selected", { value });
  
    if (value === "custom") {
      setIsCustomDate(true);
      setPendingDateRange("custom");
    } else {
      setIsCustomDate(false);
      setPendingDateRange(value);
      setPendingStartDate(null); 
      setPendingEndDate(null);  
    }
  };
 
  useEffect(() => {
    resetFilters();
    setIsCustomDate(false);
    setPendingDateRange("yesterday");
    setPendingStartDate(null);
    setPendingEndDate(null);
    setPendingResource("ALL");
    setPendingRevenue("total");
  }, [refreshCounter]);

  useEffect(() => {
    setPendingGranularity(selectedFrequency);
  }, [selectedFrequency]);

  useEffect(() => {
    setPendingResource(selectedResource);
  }, [selectedResource]);

  useEffect(() => {
    setPendingRevenue(selectedRevenue);
  }, [selectedRevenue]);

  useEffect(() => {
    const isSelectedCustomRange = customDateRangePattern.test(selectedDateRange);
    setPendingDateRange(selectedDateRange);
    setIsCustomDate(isSelectedCustomRange);

    if (!isSelectedCustomRange) {
      setPendingStartDate(null);
      setPendingEndDate(null);
      return;
    }

    const [rangeStart, rangeEnd] = selectedDateRange.split(" - ");
    const resolvedStart = startDate || rangeStart;
    const resolvedEnd = endDate || rangeEnd;
    const parsedStart = resolvedStart ? new Date(`${resolvedStart}T00:00:00`) : null;
    const parsedEnd = resolvedEnd ? new Date(`${resolvedEnd}T00:00:00`) : null;

    setPendingStartDate(
      parsedStart && !Number.isNaN(parsedStart.getTime()) ? parsedStart : null
    );
    setPendingEndDate(
      parsedEnd && !Number.isNaN(parsedEnd.getTime()) ? parsedEnd : null
    );
  }, [selectedDateRange, startDate, endDate]);

  const handleApply = () => {
    if (pendingGranularity !== selectedFrequency) {
      setSelectedFrequency(pendingGranularity);
    }
    if (pendingResource !== selectedResource) {
      setSelectedResource(pendingResource);
    }
    if (pendingRevenue !== selectedRevenue) {
      setSelectedRevenue(pendingRevenue);
    }

    if (isCustomDate) {
      if (pendingStartDate && pendingEndDate) {
        const formattedStart = format(pendingStartDate, "yyyy-MM-dd");
        const formattedEnd = format(pendingEndDate, "yyyy-MM-dd");
        const formattedRange = `${formattedStart} - ${formattedEnd}`;

        if (startDate !== formattedStart) {
          setStartDate(formattedStart);
        }
        if (endDate !== formattedEnd) {
          setEndDate(formattedEnd);
        }
        if (selectedDateRange !== formattedRange) {
          setSelectedDateRange(formattedRange);
        }
      }
    } else {
      if (selectedDateRange !== pendingDateRange) {
        setSelectedDateRange(pendingDateRange);
      }
      if (startDate !== null) {
        setStartDate(null);
      }
      if (endDate !== null) {
        setEndDate(null);
      }
    }

    triggerSearch();
  };
  
  
  return (
    <div>
     {activeTabLabel !== "Asset Revenue" && (
  <>
    <div className="bg-white p-4 sm:p-7 rounded-lg mt-8 md:mt-5 border-2 font-poppins mx-4 md:mx-10 lg:mx-12 mb-20">
    <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
      <div className="flex flex-wrap gap-4 sm:gap-6 md:gap-8 items-start flex-1">
      {/* Icon Container: Fixed, no wrap */}
      <div className="flex-shrink-0 flex items-center text-gray-600 mt-1 sm:mt-5 md:mt-8 w-8 sm:w-12">
        <TfiAlignCenter className="text-lg sm:text-3xl" />
      </div>
  
      {/* Filters Container: Can wrap */}
      <div className="flex flex-wrap gap-x-6 gap-y-4 flex-1">
        {/* {activeTabLabel === "Estimated Settlements" && ( */}
        {(activeTabLabel === "Estimated Settlements"|| activeTabLabel === "Asset Revenue") && (
          <div className="relative w-full sm:w-[250px]">
            <label className="block text-md font-medium text-gray-700 mb-1">Revenue Group</label>
            <RevenueGroupDropdown
              selectedValue={pendingRevenue}
              onChange={setPendingRevenue}
            />
          </div>
        )}
  
    <div className="relative w-full sm:w-[250px]">
          <label className="block text-md font-medium text-gray-700 mb-1">Date Range</label>
      <DateRangeDropdown
        selectedValue={isCustomDate ? "custom" : pendingDateRange}
        onChange={handleDateRangeChange}
      />
    </div>

        {isCustomDate && (
          <div className="relative w-full sm:w-[230px]">
            <label className="block text-md font-medium text-gray-700 mb-1">Custom Date Range</label>
            <div className="relative">
              <DatePicker
                selected={pendingStartDate}
                dateFormat="yyyy-MM-dd"
                onChange={(dates: [Date | null, Date | null]) => {
                  const [start, end] = dates;
                  setPendingStartDate(start);
                  setPendingEndDate(end);
                  if (start && end) {
                    const formattedStart = format(start, "yyyy-MM-dd");
                    const formattedEnd = format(end, "yyyy-MM-dd");
                    setPendingDateRange(`${formattedStart} - ${formattedEnd}`);
                  }
                }}
                startDate={pendingStartDate}
                endDate={pendingEndDate}
                selectsRange
                className="w-full min-w-[230px] max-w-[250px] p-1 sm:p-3 pl-4 pr-16 border border-gray-300 rounded-lg bg-[#f6f6f6] text-[#000000] shadow-inner text-xs md:text-base appearance-none"
                placeholderText="Select Date Range"
              />
            </div>
          </div>
        )}
  
    <div className="relative w-full sm:w-[250px]">
          <label className="block text-md font-medium text-gray-700 mb-1">Granularity</label>
      <GranularityDropdown
        selectedValue={pendingGranularity}
        onChange={setPendingGranularity}
      />
    </div>

    <div className="relative w-full sm:w-[250px]">
          <label className="block text-md font-medium text-gray-700 mb-1">Resources</label>
      <ResourceDropdown
        selectedValue={pendingResource || "ALL"}
        onChange={setPendingResource}
      />
    </div>

    <div className="w-full sm:w-auto">
      <label className="block text-md font-medium text-gray-700 mb-1 invisible">Apply</label>
      <button
        onClick={handleApply}
        className="flex items-center gap-2 px-5 h-[38px] bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-colors duration-200"
      >
        <FiSearch className="text-base" />
        Apply
      </button>
    </div>
      </div>
    </div>
      <div className="w-full xl:w-auto xl:shrink-0 flex flex-wrap xl:flex-nowrap items-start gap-4 xl:gap-6">
        {(activeTabLabel === "Price" || activeTabLabel === "Volume" || activeTabLabel === "Estimated Settlements") && (
          <div className="relative w-full sm:w-[250px]">
            <IntervalAlignmentToggle />
          </div>
        )}
        <div className="relative w-full sm:w-auto">
          <GranularityAggregationToggle className="w-full" />
        </div>
      </div>
    </div>
      {/* </> 
)} */}
  </div>
  </>
)}
</div>
    );
};

export default AssetFilters;
